import { useEffect, useState } from "react";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Mail, Phone, MapPin, ArrowUpRight, Linkedin, User } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

// DICIONÁRIO DE TRADUÇÕES
const TRANSLATIONS = {
  en: {
    heroEyebrow: "Contact",
    heroSubtitle: "Use whichever channel works best for you.",
    leftEyebrow: "Where we are",
    leftDesc: "Engineering headquarters in central Portugal, with development and commissioning teams travelling across entire Europe.",
    openMaps: "Open in Maps",
    linkedinBtn: "Follow on LinkedIn",
    salesEyebrow: "Sales contact",
    salesRole: "Sales Director",
    salesDesc: "For commercial enquiries, proposals and new projects.",
    channels: {
      email: "General email",
      phone: "Phone",
    },
    floorDetails: "Rua D. Manuel I, 78, Level 2, Room 1"
  },
  pt: {
    heroEyebrow: "Contactos",
    heroSubtitle: "Escolha o canal de comunicação que lhe for mais conveniente.",
    leftEyebrow: "Onde estamos",
    leftDesc: "Sede de engenharia no centro de Portugal, com equipas de desenvolvimento e comissionamento presentes em toda a Europa.",
    openMaps: "Abrir no Maps",
    linkedinBtn: "Seguir no LinkedIn",
    salesEyebrow: "Contacto comercial",
    salesRole: "Diretor Comercial",
    salesDesc: "Para consultas comerciais, propostas e novos projetos.",
    channels: {
      email: "E-mail geral",
      phone: "Telefone",
    },
    floorDetails: "Rua D. Manuel I, 78, Piso 2, Sala 1"
  },
};

export default function Contact() {
  const { language } = useLanguage();
  const t = TRANSLATIONS[language];

  // Canais gerados dinamicamente para traduzir as labels
  const channels = [
    {
      icon: Mail,
      label: t.channels.email,
      value: "info@wsbp.eu",
      href: "mailto:info@wsbp.eu",
      testId: "contact-email-info",
    },
    {
      icon: Phone,
      label: t.channels.phone,
      value: "+351 239 163 715",
      href: "tel:+351239163715",
      testId: "contact-phone-reception",
    },
  ];

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow={t.heroEyebrow}
        title={
          language === "pt" ? (
            <>
              Vamos <span className="text-[#F68A31]">construir</span>.
            </>
          ) : (
            <>
              Let's <span className="text-[#F68A31]">build</span>.
            </>
          )
        }
        subtitle={t.heroSubtitle}
        image="https://images.pexels.com/photos/5203849/pexels-photo-5203849.jpeg"
      />

      <section className="relative z-10 mx-auto max-w-[1400px] px-6 py-8 md:px-12 lg:px-16">
        <div className="grid gap-12 lg:grid-cols-12">
          {/* LEFT — Address & LinkedIn */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="eyebrow">{t.leftEyebrow}</div>
              <h2 className="font-display mt-6 text-4xl font-bold tracking-tight sm:text-5xl">
                Coimbra · Portugal
              </h2>
              <p className="mt-6 text-lg text-white/75">
                {t.leftDesc}
              </p>
              <a
                href="https://maps.app.goo.gl/ezyV6HiT7FZBd6od7"
                target="_blank"
                rel="noreferrer"
                className="mt-8 block rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-[#F68A31]/60"
                data-testid="address-card"
              >
                <div className="flex items-start gap-3">
                  <MapPin className="mt-1 text-[#F68A31]" size={20} />
                  <div>
                    <div className="font-display text-base font-semibold leading-snug">
                      Estádio Cidade de Coimbra
                    </div>
                    <div className="text-sm leading-relaxed text-white/70">
                      {t.floorDetails}
                      <br />
                      3030-327 Coimbra · Portugal
                    </div>
                    <div className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-[0.24em] text-[#F68A31]">
                      {t.openMaps} <ArrowUpRight size={12} />
                    </div>
                  </div>
                </div>
              </a>
              <a
                href="https://www.linkedin.com/company/wsbp-electronics/"
                target="_blank"
                rel="noreferrer"
                className="btn-secondary mt-6 text-sm"
                data-testid="contact-linkedin"
              >
                <Linkedin size={16} /> {t.linkedinBtn}
              </a>
            </Reveal>
          </div>

          {/* RIGHT — Channels */}
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="grid gap-4 sm:grid-cols-2">
                {channels.map((c) => {
                  const IconComponent = c.icon;
                  return (
                    <a
                      key={c.label}
                      href={c.href}
                      data-testid={c.testId}
                      className="group block rounded-2xl border border-white/10 bg-white/[0.03] p-7 transition-colors hover:border-[#F68A31]/60"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#F68A31]/10 text-[#F68A31] transition-colors group-hover:bg-[#F68A31] group-hover:text-black">
                          <IconComponent size={20} />
                        </div>
                        <ArrowUpRight
                          size={18}
                          className="text-white/40 transition-all group-hover:text-[#F68A31] group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                        />
                      </div>
                      <div className="mt-6 text-xs uppercase tracking-[0.24em] text-white/55">
                        {c.label}
                      </div>
                      <div className="font-display mt-2 text-2xl font-semibold">
                        {c.value}
                      </div>
                    </a>
                  );
                })}
              </div>
            </Reveal>

            {/* Sales contact - featured */}
            <Reveal delay={0.2}>
              <div
                className="mt-8 overflow-hidden rounded-3xl border border-[#F68A31]/30 bg-gradient-to-br from-[#F68A31]/15 via-black/40 to-black p-10"
                data-testid="sales-contact-card"
              >
                <div className="flex items-center gap-3">
                  <div className="eyebrow">{t.salesEyebrow}</div>
                </div>
                <div className="mt-6 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F68A31] text-black">
                        <User size={22} strokeWidth={2.5} />
                      </div>
                      <div>
                        <h3 className="font-display text-3xl font-bold leading-none sm:text-4xl">
                          Nuno Correia
                        </h3>
                        <p className="mt-2 text-sm uppercase tracking-[0.24em] text-[#F68A31]">
                          {t.salesRole}
                        </p>
                      </div>
                    </div>
                    <p className="mt-5 max-w-lg text-white/75">
                      {t.salesDesc}
                    </p>
                  </div>
                  <div className="flex w-full max-w-sm flex-col items-stretch gap-3 md:w-auto md:min-w-[280px]">
                    <a
                      href="tel:+351911706720"
                      data-testid="contact-sales-phone"
                      className="btn-primary justify-center text-sm"
                    >
                      <Phone size={16} /> +351 911 706 720
                    </a>
                    <a
                      href="mailto:nuno.correia@wsbp.eu"
                      data-testid="contact-sales-email"
                      className="btn-secondary justify-center text-sm"
                    >
                      <Mail size={16} /> nuno.correia@wsbp.eu
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}